import {ref} from "vue";

/**
 * A composable function to provide easy access to url search parameters
 * 
 * @return an object with functions to get, set and clear url parameters
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams URLSearchParams
 */
export const useUrlSearchParams = () => {
    
    let url = new URL(window.location.href);

    /**
     * Set a parameter in url parameters
     * 
     * @param key the key of the parameter
     * @param value the value of the parameter
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams URLSearchParams
     */
    const setParam = (key: string, value?: string) => {
        if (!value) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
        pushState();
    }

    /**
     * Set an array of parameters in url parameters 
     * 
     * @param key the key of the parameter
     * @param values the values of the parameter
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams URLSearchParams
     */
    const setParamArray = (key: string, values: string[]) => {
        url.searchParams.delete(key);
        values.forEach(value => url.searchParams.append(key, value));
        window.history.pushState({}, '', url.toString());
        pushState();
    }
    
    /**
     * Clear all parameters from url parameters
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams URLSearchParams
     */
    const clearParams = () => {
        url.search = '';
        pushState();
    }

    /**
     * Get a parameter from url parameters
     * 
     * @param key the key of the parameter
     * @return the value of the parameter or null if the parameter does not exist
     * @see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams URLSearchParams
     */
    const getParam = (key: string) => {
        return url.searchParams.get(key);
    }

    /**
     * Set the new url parameter state to the url, and push it to the history
     * 
     * @see https://developer.mozilla.org/en-US/docs/Web/API/History/pushState window.history.pushState
     */
    const pushState = () => {
        window.history.pushState({}, '', url.toString());
    }

    /**
     * Refresh the url to sync with the current window location, useful when the url is changed by other means
     */
    const refresh = () => {
        url = new URL(window.location.href);
    }
    
    return {
        setParam,
        setParamArray,
        clearParams,
        getParam,
        refresh
    }
}

export const useHash = () => {
    const hashWithoutHashtag = () => {
        return window.location.hash.replace('#', '');
    }
    
    const hashRef = ref(hashWithoutHashtag() || undefined);
    
    const setHash = (hash: string) => {
        hashRef.value = hash;
        window.location.hash = hash;
    }
    
    const clearHash = () => {
        hashRef.value = undefined;
        history.pushState("", document.title, window.location.pathname + window.location.search);
    }
    
    return {
        hash: hashRef,
        setHash,
        clearHash
    }
}